import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { CheckCircle, Award, Shield, FileText, ZoomIn, X, ChevronDown } from 'lucide-react';

export default function Sertifikater() {
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setEnlargedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const certifications = [
    {
      icon: Award,
      title: 'StartBANK Sertifikat',
      subtitle: 'Offisiell leverandør',
      description: 'LYKKE ENTREPRENØR AS er registrert som godkjent leverandør på StartBANK-nettverket. Dette sikrer at vi oppfyller høyeste standarder for kvalitet og profesjonalisme.',
      details: [
        'AchillesID: 00227993',
        'Gyldig inntil: 5. april 2027',
        'Sertifikat generert: 2. januar 2026',
        'Fagkompetanse: Snekkerarbeider (1.12.1)'
      ],
      color: 'from-[#a6b894] to-[#a6b894]',
      borderColor: 'border-[#a6b894]/40'
    },
    {
      icon: Shield,
      title: 'HMS SYSTEM',
      subtitle: 'Helse, miljø og sikkerhet',
      description: 'Vi har implementert et komprehensivt HMS-system som sikrer høy sikkerhet og trivsel for alle som er involvert i våre prosjekter. Alt arbeid følger strenge sikkerhetsstandarder.',
      details: [
        'Rutiner for arbeidssikkerhet',
        'Miljøansvar og bærekraft',
        'Regelmessig opplæring og kontroller',
        'Dokumentasjon og rapportering'
      ],
      color: 'from-[#a6b894] to-[#a6b894]',
      borderColor: 'border-[#a6b894]/40'
    },
    {
      icon: FileText,
      title: 'KS SYSTEM',
      subtitle: 'Kvalitetsstyringsystem',
      description: 'Vårt kvalitetsstyringsystem sikrer konsistent leveranse av høyeste kvalitet. Hver fase av produksjon og installasjon overvåkes nøye.',
      details: [
        'Prosedyrer for kvalitetskontroll',
        'Dokumentert produksjonsprosess',
        'Løpende forbedringer',
        'Kundetilfredshet og feedback'
      ],
      color: 'from-[#a6b894] to-[#a6b894]',
      borderColor: 'border-[#a6b894]/40'
    },
    {
      icon: FileText,
      title: 'FDV Dokumentasjon',
      subtitle: 'Forvaltnings-, drifts- og vedlikeholdsdokumentasjon',
      description: 'Hver badstue leveres med komplett FDV-dokumentasjon i henhold til norsk standard. Dette gir deg all informasjonen du trenger for vedlikehold og drift.',
      details: [
        'Drift- og vedlikeholdsveiledninger',
        'Tekniske spesifikasjoner',
        'Garantidokumenter',
        'Digitalt tilgjengelig via Boligmappa'
      ],
      color: 'from-[#a6b894] to-[#a6b894]',
      borderColor: 'border-[#a6b894]/40'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-32 text-center"
          >
            <p className="font-body text-base tracking-widest uppercase text-primary mb-4">Sertifiseringer & Systemer</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-tight mb-8">
              Sertifikat & Standarder
            </h1>
            <p className="font-serif text-2xl text-foreground/70 leading-relaxed max-w-2xl mx-auto">
              Vi oppfyller høyeste norske og internasjonale standarder for kvalitet, sikkerhet og profesjonalisme. Alle våre prosjekter er dokumentert og sertifisert.
            </p>
          </motion.div>

          {/* Certifications Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-32">
            {certifications.map((cert, idx) => {
              const Icon = cert.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className={`rounded-[2px] border-2 border-[#a6b894] bg-white p-8 shadow-sm hover:bg-[#a6b894]/10 hover:shadow-md hover:-translate-y-1 active:scale-95 active:shadow-none transition-all duration-300 cursor-default`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <Icon size={32} className="text-primary flex-shrink-0 mt-1" />
                    <div className="break-words">
                      <h3 className="font-heading text-2xl font-light mb-1 break-words">{cert.title}</h3>
                      <p className="font-body text-base tracking-widest uppercase text-primary break-words">{cert.subtitle}</p>
                    </div>
                  </div>

                  <p className="font-serif text-2xl text-foreground/70 leading-relaxed mb-6">
                    {cert.description}
                  </p>

                  <div className="space-y-3 pt-6 border-t border-border/30">
                    {cert.details.map((detail, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle size={16} className="text-primary flex-shrink-0 mt-1" />
                        <span className="font-serif text-2xl text-foreground/70">{detail}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* StartBANK Certificate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-32"
          >
            <h2 className="font-heading text-4xl font-light mb-16">StartBANK Sertifikat</h2>

            <div className="grid md:grid-cols-2 gap-16 items-center mb-16">
              <div>
                <h3 className="font-heading text-2xl font-light mb-6">Hva betyr dette?</h3>
                <p className="font-serif text-2xl text-foreground/70 leading-relaxed mb-8">
                  StartBANK er Norges største leverandørnettverk for byggebransjen. Å være registrert som leverandør på StartBANK betyr at vi har gjennomgått grundige kontroller og oppfyller høye standarder for kvalitet, sikkerhet og økonomi.
                </p>
                <p className="font-serif text-2xl text-foreground/70 leading-relaxed">
                  Dette gir kunder og samarbeidspartnere sikkerhet for at vi er en tillitverdig og seriøs leverandør. StartBANK-registrering krever at vi vedlikeholder høy standard på alle områder av virksomheten.
                </p>
              </div>

              <div className="relative rounded-lg overflow-hidden h-96 bg-secondary/20 group cursor-pointer">
                <img
                  src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/0d445284f_Cert_StartBANK_Registered_Side_1.jpg"
                  alt="StartBANK Sertifikat"
                  className="w-full h-full object-cover"
                  onClick={() => setEnlargedImage('https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/0d445284f_Cert_StartBANK_Registered_Side_1.jpg')}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <ZoomIn size={40} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </div>

            <div className="bg-[#2a1810] text-background rounded-[6px] p-8">
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div>
                  <p className="font-body text-base tracking-widest uppercase text-primary/80 mb-2">AchillesID</p>
                  <p className="font-heading text-2xl font-light">00227993</p>
                </div>
                <div>
                  <p className="font-body text-base tracking-widest uppercase text-primary/80 mb-2">Gyldig Til</p>
                  <p className="font-heading text-2xl font-light">5. april 2027</p>
                </div>
                <div>
                  <p className="font-body text-base tracking-widest uppercase text-primary/80 mb-2">Fagkompetanse</p>
                  <p className="font-heading text-2xl font-light">Snekkerarbeider</p>
                </div>
              </div>
              <a
                href="https://media.base44.com/files/public/69cd72579c1c23d07f425ea3/258d850b4_Cert_StartBANK_Registered.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-background text-foreground font-body tracking-widest text-xs font-semibold rounded-sm hover:bg-background/90 transition-colors"
              >
                LAST NED SERTIFIKAT
              </a>
            </div>
          </motion.section>

          {/* Why Certifications Matter */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-32 border-t border-border/40 pt-16"
          >
            <h2 className="font-heading text-4xl font-light mb-16">Hvorfor er dette viktig?</h2>

            <div className="border-t border-border/40">
              {[
                {
                  title: 'Kvalitetssikring',
                  desc: 'Våre systemer sikrer at hver badstue produseres og installeres med millimeterpresisjon og høyeste kvalitet.'
                },
                {
                  title: 'Sikkerhet',
                  desc: 'HMS-systemet vårt sikrer at alle arbeidere og brukere er trygge. Vi følger strenge sikkerhetsprotokoller på alle stadier.'
                },
                {
                  title: 'Ansvar',
                  desc: 'Sertifikasjonene viser at vi tar ansvar for miljøet, økonomien og samfunnet. Vi er transparent og etisk.'
                },
                {
                  title: 'Kundegaranti',
                  desc: 'Med komplett FDV-dokumentasjon får du all informasjonen du trenger. Garantier og vedlikeholdsveiledninger er klare og tilgjengelige.'
                }
              ].map((item, idx) => (
                <div key={idx} className="border-b border-border/40">
                  <button
                    onClick={() => setOpenAccordion(openAccordion === idx ? null : idx)}
                    className="w-full flex items-center justify-between py-6 text-left group"
                  >
                    <h3 className="font-heading text-2xl font-semibold group-hover:text-primary transition-colors">{item.title}</h3>
                    <ChevronDown
                      size={20}
                      className={`text-primary flex-shrink-0 transition-transform duration-200 ${openAccordion === idx ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openAccordion === idx && (
                    <div className="pb-6">
                      <p className="font-serif text-2xl text-foreground/70 leading-relaxed">{item.desc}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>

          {/* Standards Compliance */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-20"
          >
            <h2 className="font-heading text-4xl font-light mb-16">Norske Standarder</h2>

            <div className="border-t border-border/40">
              {[
                { standard: 'NS3940', desc: 'Norm for Forvaltnings-, drifts- og vedlikeholdsdokumentasjon' },
                { standard: 'NS-EN ISO 9001', desc: 'Internasjonalt kvalitetsstyringsstandard' },
                { standard: 'Arbeidsmiljøloven', desc: 'Alle krav til sikkerhet og helse på arbeidsplassen oppfylles' }
              ].map((item, idx) => (
                <div key={idx} className="border-b border-border/40">
                  <button
                    onClick={() => setOpenAccordion(openAccordion === `std-${idx}` ? null : `std-${idx}`)}
                    className="w-full flex items-center justify-between py-6 text-left group"
                  >
                    <p className="font-heading text-2xl tracking-widest uppercase font-semibold" style={{color: '#2a1810'}}>{item.standard}</p>
                    <ChevronDown size={20} className={`text-primary flex-shrink-0 transition-transform duration-200 ${openAccordion === `std-${idx}` ? 'rotate-180' : ''}`} />
                  </button>
                  {openAccordion === `std-${idx}` && (
                    <div className="pb-6">
                      <p className="font-serif text-2xl text-foreground/70">{item.desc}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-[#2a1810] text-background rounded-[6px] p-16 text-center"
          >
            <h2 className="font-heading text-3xl font-light mb-6">Vilkår for Kvalitet</h2>
            <p className="font-serif text-2xl text-background/80 mb-10">
              Alle våre sertifikater og systemer er til for at din badstue skal være sikker, vakker og holdbar i generasjoner.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a
                href="/handverket"
                className="px-8 py-3 bg-background text-foreground font-sans tracking-widest text-sm font-semibold rounded-sm hover:bg-background/90 transition-colors"
              >
                LES OM HÅNDVERKET
              </a>
              <button
                onClick={() => setContactModalOpen(true)}
                className="px-8 py-3 border-2 border-background text-background font-sans tracking-widest text-sm font-semibold rounded-sm hover:bg-background/10 transition-colors"
                style={{outline: 'none'}}
              >
                SPØRSMÅL?
              </button>
            </div>
          </motion.section>
        </div>
      </div>

      {enlargedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setEnlargedImage(null)}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-4xl w-full"
          >
            <img src={enlargedImage} alt="Powiększony certyfikat" className="w-full h-auto rounded-lg" />
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
          </motion.div>
        </motion.div>
      )}

      <Footer />

      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </div>
  );
}